<template>
  <AppLayout>
    <div class="pacientes">
      <!-- HEADER similar a Agenda -->
      <header class="topbar">
        <h1>Pacientes</h1>
        <button class="btn-menu" @click="abrirPerfil" aria-label="Abrir perfil">
          <span></span><span></span><span></span>
        </button>
      </header>

      <!-- LOADING Y ERROR -->
      <div v-if="store.loading" style="text-align:center; margin: 32px 0;">
        <span>Cargando pacientes...</span>
      </div>
      <div v-if="store.error" style="color:#e74c3c; text-align:center; margin: 16px 0;">
        {{ store.error }}
        <br v-if="store.modoOffline" />
        <button
          v-if="store.modoOffline"
          @click="store.reintentarConexion()"
          style="margin-top:8px; padding:6px 16px; background:#678AB2; color:white; border:none; border-radius:6px; cursor:pointer; font-weight:600; font-size:13px;"
        >
          Reintentar conexión
        </button>
      </div>

      <!-- SIEMPRE MOSTRAR UI -->
      <!-- BUSCADOR -->
      <div class="campo-buscador">
        <!-- autocomplete="off" a propósito: sin esto el navegador guarda en su historial los
             nombres de pacientes tipeados y los sugiere después, incluso a otra persona que use
             la misma computadora. -->
        <input
          type="search"
          id="buscar-paciente"
          name="buscar-paciente"
          autocomplete="off"
          placeholder="Buscar paciente..."
          class="buscador"
          v-model="busqueda"
        />
      </div>
      <!-- NUEVO PACIENTE (botón flotante) -->
      <button class="btn-plus" @click="showNuevoPaciente = true">+</button>

      <!-- LISTA -->
      <section class="lista">
        <router-link
          v-for="p in pacientesActivos"
          :key="p.id"
          :to="`/paciente/${p.id}`"
          class="paciente"
        >
          <div class="info">
            <span class="paciente-nombre" :class="{ 'tachado': p.activo === false }">{{ p.nombre }}</span>
            <span v-if="p.activo === false" class="tag-inactivo">(Deshabilitado)</span>
          </div>
          <span class="flecha">›</span>
        </router-link>
        <div v-if="pacientesActivos.length === 0" style="text-align:center; color:#888; margin:32px 0;">No hay pacientes activos.</div>
      </section>

      <!-- SECCIÓN DESHABILITADOS -->
      <div v-if="pacientesInactivos.length > 0" class="inactivos-container">
        <button class="btn-toggle-inactivos" @click="mostrarInactivos = !mostrarInactivos">
          {{ mostrarInactivos ? 'Ocultar' : 'Ver' }} Pacientes Deshabilitados ({{ pacientesInactivos.length }})
        </button>
        
        <section v-if="mostrarInactivos" class="lista lista-inactivos">
          <router-link
            v-for="p in pacientesInactivos"
            :key="p.id"
            :to="`/paciente/${p.id}`"
            class="paciente inactivo"
          >
            <div class="info">
              <span class="paciente-nombre tachado">{{ p.nombre }}</span>
            </div>
            <span class="flecha">›</span>
          </router-link>
        </section>
      </div>
    </div>

    <!-- MODAL -->
    <NuevoPacienteModal
      v-if="showNuevoPaciente"
      @cerrar="showNuevoPaciente = false"
    />
  </AppLayout>
</template>

<script setup>
import AppLayout from "@/layouts/AppLayout.vue";
import NuevoPacienteModal from "@/components/NuevoPacienteModal.vue";
import { ref, onMounted, computed } from "vue";
import { usePerfilPanel } from "@/composables/usePerfilPanel";
const { abrir: abrirPerfil } = usePerfilPanel();
import { usePacientesStore } from "@/stores/pacientes";

const store = usePacientesStore();
const showNuevoPaciente = ref(false);
const busqueda = ref("");
const mostrarInactivos = ref(false);

onMounted(() => {
  store.cargarDesdeLocalStorage();
});

const pacientesFiltrados = computed(() => {
  let lista = store.pacientes.slice();
  if (busqueda.value) {
    const term = busqueda.value.toLowerCase();
    lista = lista.filter(p => (p.nombre || '').toLowerCase().includes(term));
  }
  return lista.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
});

const pacientesActivos = computed(() => pacientesFiltrados.value.filter(p => p.activo !== false));
const pacientesInactivos = computed(() => pacientesFiltrados.value.filter(p => p.activo === false));

</script>

<style scoped>
.pacientes {
  padding: 16px;
  padding-bottom: 80px;
  max-width: 420px;
  margin: auto;
  font-family: system-ui, sans-serif;
}

/* HEADER */
.topbar {
  position: relative;
  height: 56px;
  background: #678AB2;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border-radius: 12px;
  margin-bottom: 12px;
}

.btn-menu {
  position: absolute;
  right: 16px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s;
}

.btn-menu:hover {
  background: rgba(255, 255, 255, 0.3);
}

.btn-menu span {
  display: block;
  width: 16px;
  height: 2px;
  background: white;
  border-radius: 2px;
}

.topbar h1 {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}

/* BUSCADOR */
.campo-buscador {
  width: 100%;
}

.buscador {
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: none;
  background: #f1f1f1;
  margin-bottom: 12px;
  box-sizing: border-box;
  font-size: 16px;
}

/* BOTÓN */
.btn-plus {
  position: fixed;
  bottom: 110px;
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #678AB2;
  color: white;
  font-size: 28px;
  font-weight: bold;
  border: none;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(103, 138, 178, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

/* LISTA */
.lista {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.paciente {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  padding: 16px;
  border-radius: 12px;
  text-decoration: none;
  color: #333;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  transition: all 0.2s ease;
}

.paciente:hover {
  border-color: #678AB2;
  box-shadow: 0 4px 12px rgba(103, 138, 178, 0.15);
  transform: translateY(-1px);
}

.paciente-nombre {
  font-size: 18px;
  font-weight: 600;
  color: #678AB2;
}

.flecha {
  font-size: 22px;
  color: #bbb;
}

.inactivos-container {
  margin-top: 24px;
  border-top: 1px dashed #ccc;
  padding-top: 16px;
}

.btn-toggle-inactivos {
  background: none;
  border: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  width: 100%;
  text-align: center;
  padding: 8px;
  margin-bottom: 8px;
}

.lista-inactivos .paciente {
  background: #f0f0f0;
  opacity: 0.8;
}

/* Tablet */
@media (min-width: 768px) {
  .pacientes {
    padding: 24px;
    padding-bottom: 80px;
    max-width: 800px;
  }

  .topbar {
    height: 64px;
    margin-bottom: 16px;
  }

  .topbar h1 {
    font-size: 24px;
    font-weight: 700;
  }

  .buscador {
    padding: 14px;
    font-size: 16px;
    margin-bottom: 16px;
  }

  .paciente {
    padding: 16px;
  }
}

/* Desktop */
@media (min-width: 1024px) {
  .pacientes {
    padding: 32px;
    padding-bottom: 80px;
    max-width: 1200px;
  }

  .topbar {
    height: 64px;
    margin-bottom: 20px;
  }

  .topbar h1 {
    font-size: 28px;
    font-weight: 700;
  }

  .buscador {
    padding: 16px;
    font-size: 16px;
    margin-bottom: 20px;
  }

  .lista {
    gap: 12px;
  }

  .paciente {
    padding: 18px;
  }
}

.tachado {
  text-decoration: line-through;
  opacity: 0.6;
}

.tag-inactivo {
  font-size: 12px;
  color: #e74c3c;
  margin-left: 6px;
}
</style>
